/**
 * JourneyRoadmap Component
 * Implementation timeline and milestones
 */

import React from 'react';

const styles = {
  container: {
    padding: '60px 20px',
    marginBottom: '40px',
  },
  title: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '12px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '16px',
    color: '#BBBEC7',
    textAlign: 'center',
    marginBottom: '60px',
  },
  timeline: {
    maxWidth: '900px',
    margin: '0 auto',
    position: 'relative',
    paddingTop: '30px',
    paddingBottom: '30px',
  },
  timelineTrack: {
    position: 'absolute',
    left: '50%',
    top: 0,
    bottom: 0,
    width: '2px',
    backgroundColor: '#2A3444',
    transform: 'translateX(-50%)',
  },
  phase: {
    marginBottom: '40px',
    position: 'relative',
  },
  phaseOdd: {
    paddingRight: '50%',
    paddingLeft: '0',
    textAlign: 'right',
  },
  phaseEven: {
    paddingLeft: '50%',
    paddingRight: '0',
    textAlign: 'left',
  },
  phaseDot: {
    position: 'absolute',
    left: '50%',
    top: '0',
    width: '16px',
    height: '16px',
    backgroundColor: '#0057FF',
    border: '3px solid #0F1729',
    borderRadius: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2,
  },
  phaseCard: {
    padding: '24px',
    borderRadius: '8px',
    border: '1px solid #2A3444',
    backgroundColor: '#1A2332',
    transition: 'all 0.3s ease',
  },
  phaseCardHover: {
    borderColor: '#0057FF',
    backgroundColor: 'rgba(0, 87, 255, 0.05)',
    boxShadow: '0 8px 16px rgba(0, 87, 255, 0.15)',
  },
  phaseMonth: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#00D4AA',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  phaseTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: '12px',
  },
  phaseDescription: {
    fontSize: '14px',
    color: '#BBBEC7',
    marginBottom: '16px',
    lineHeight: '1.5',
  },
  milestoneList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  milestoneItem: {
    fontSize: '13px',
    color: '#BBBEC7',
    paddingLeft: '20px',
    marginBottom: '8px',
    position: 'relative',
  },
  milestoneCheck: {
    position: 'absolute',
    left: 0,
    color: '#00D4AA',
    fontWeight: 'bold',
  },
  '@media (max-width: 768px)': {
    timelineTrack: {
      left: '12px',
    },
    phaseDot: {
      left: '12px',
    },
    phaseOdd: {
      paddingRight: '0',
      paddingLeft: '50px',
      textAlign: 'left',
    },
    phaseEven: {
      paddingLeft: '50px',
      paddingRight: '0',
      textAlign: 'left',
    },
  },
};

/**
 * Phase component for individual timeline items
 */
function TimelinePhase({ phase, index }) {
  const [isHovered, setIsHovered] = React.useState(false);
  const isOdd = index % 2 === 0;

  return (
    <div
      style={{
        ...styles.phase,
        ...(isOdd ? styles.phaseOdd : styles.phaseEven),
      }}
    >
      <div style={styles.phaseDot} />
      <div
        style={{
          ...styles.phaseCard,
          ...(isHovered && styles.phaseCardHover),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.phaseMonth}>{phase.month}</div>
        <h3 style={styles.phaseTitle}>{phase.title}</h3>
        <p style={styles.phaseDescription}>{phase.description}</p>
        {phase.milestones && (
          <ul style={styles.milestoneList}>
            {phase.milestones.map((milestone, idx) => (
              <li key={idx} style={styles.milestoneItem}>
                <span style={styles.milestoneCheck}>✓</span>
                {milestone}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * JourneyRoadmap Component
 */
function JourneyRoadmap({ prospect }) {
  const phases = [
    {
      month: 'Week 1-2',
      title: 'Discovery & Setup',
      description: 'We learn about your business and set up your account.',
      milestones: [
        'Initial kickoff meeting',
        'Environment configuration',
        'Team training scheduled',
      ],
    },
    {
      month: 'Week 3-4',
      title: 'Integration',
      description: 'Connect Talk Australia with your existing systems.',
      milestones: [
        'System integrations',
        'Data migration',
        'Testing and validation',
      ],
    },
    {
      month: 'Month 2',
      title: 'Training & Onboarding',
      description: 'Your team gets trained on the platform.',
      milestones: [
        'Department training sessions',
        'Best practices workshop',
        'Documentation provided',
      ],
    },
    {
      month: 'Month 3',
      title: 'Launch & Go-Live',
      description: 'Your team starts using Talk Australia.',
      milestones: [
        'Full platform launch',
        'Live support activation',
        'Performance monitoring begins',
      ],
    },
    {
      month: 'Month 4+',
      title: 'Optimization & Growth',
      description: 'We continuously improve and expand your usage.',
      milestones: [
        'Ongoing optimization',
        'Advanced features rollout',
        'Quarterly business reviews',
      ],
    },
  ];

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Your Implementation Journey</h2>
      <p style={styles.subtitle}>A clear timeline for getting you up and running</p>

      <div style={styles.timeline}>
        <div style={styles.timelineTrack} />
        {phases.map((phase, idx) => (
          <TimelinePhase key={idx} phase={phase} index={idx} />
        ))}
      </div>

      {/* Summary */}
      <div
        style={{
          maxWidth: '600px',
          margin: '60px auto 0',
          padding: '24px',
          backgroundColor: '#1A2332',
          borderRadius: '8px',
          border: '1px solid #2A3444',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '14px', color: '#BBBEC7', marginBottom: '12px' }}>
          Total Implementation Time
        </div>
        <div
          style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#00D4AA',
          }}
        >
          4-6 Weeks
        </div>
        <div style={{ fontSize: '13px', color: '#888', marginTop: '8px' }}>
          From initial setup to full go-live
        </div>
      </div>
    </div>
  );
}

JourneyRoadmap.propTypes = {
  prospect: function (props, propName) {
    if (props[propName] != null && typeof props[propName] !== 'object') {
      return new Error(`${propName} must be an object`);
    }
  },
};

export default JourneyRoadmap;
